import camelCase from 'camelcase';
import snakecaseKeys from 'snakecase-keys';

import { OrderItemStatus } from '@hcs/types';
import { camelCaseToSnakeCase } from '@hcs/utils';

import {
  OrderSubmitCsvError,
  OrderSubmitEntryError,
  ValidationErrorAddressEntry,
  ValidationErrorCsv,
} from '../api';
import {
  ORDER_FILTER_IDS,
  ORDER_SET_TO_STATUSES,
  ORDERS_PER_PAGE,
} from '../constants';
import {
  OmOrder,
  OrderProgressType,
  OrdersParams,
  OrdersParamsForApi,
  OrderStatus,
  OrderTypeDescriptor,
  StatusGroup,
} from '../types';

export const prepOrdersParamsForApi = ({
  sortBy,
  sortOrder,
  ...params
}: OrdersParams): URLSearchParams => {
  const urlSearchParams = new URLSearchParams();
  const paramsForApi: OrdersParamsForApi = snakecaseKeys({
    ...params,
    pageSize: params?.pageSize || ORDERS_PER_PAGE,
    ...(sortBy
      ? {
          ordering: `${sortOrder === 'DESC' ? '-' : ''}${camelCaseToSnakeCase(
            sortBy,
          )}`,
        }
      : {}),
  });

  let prop: keyof OrdersParamsForApi;
  for (prop in paramsForApi) {
    if (prop === ORDER_FILTER_IDS.status) {
      const value = paramsForApi[prop];
      if (value) {
        const selectedOrderStatusArr: OrderStatus[] =
          ORDER_SET_TO_STATUSES[value];
        for (const param of selectedOrderStatusArr) {
          urlSearchParams.append(prop, param);
        }
      }
    } else {
      const value = paramsForApi[prop];
      if (value) {
        urlSearchParams.append(prop, value.toString());
      }
    }
  }

  return urlSearchParams;
};

export const orderTypeDescriptorValuesSnakeToCamel = (
  orderTypeSnake: OrderTypeDescriptor,
) => {
  orderTypeSnake.shownFields = orderTypeSnake.shownFields.map(
    (fieldName: string) => camelCase(fieldName),
  );
  orderTypeSnake.requiredFields = orderTypeSnake.requiredFields.map(
    (fieldName: string) => camelCase(fieldName),
  );
  orderTypeSnake.shownItemFields = orderTypeSnake.shownItemFields.map(
    (fieldName: string) => camelCase(fieldName),
  );
  orderTypeSnake.requiredItemFields = orderTypeSnake.requiredItemFields.map(
    (fieldName: string) => camelCase(fieldName),
  );
  return orderTypeSnake;
};

const NUMBER_OF_ERRORS_TO_SHOW = 4;

// if number of messages is too many, add one that says (n) more errors
const getErrorMessagesToShow = (errorMessages: string[]): string[] => {
  const errorMessagesToShow: string[] = [];
  if (errorMessages.length > 0) {
    if (errorMessages.length > NUMBER_OF_ERRORS_TO_SHOW) {
      errorMessagesToShow.push(
        ...errorMessages.slice(0, NUMBER_OF_ERRORS_TO_SHOW),
      );
      errorMessagesToShow.push(
        `(${errorMessages.length - NUMBER_OF_ERRORS_TO_SHOW}) more errors`,
      );
    } else {
      errorMessagesToShow.push(...errorMessages);
    }
  }
  return errorMessagesToShow;
};

export const getHeaderStatusGroupsDisplay = (
  order: OmOrder,
  orderProgress: OrderProgressType,
) => {
  return order.orderTypeDescriptor.headerStatusGroups.map(
    (headerStatusGroup: StatusGroup) => {
      const statusGroupCount: number = Object.keys(orderProgress).reduce(
        (count, orderItemStatus) => {
          if (
            headerStatusGroup.includedStatuses.indexOf(orderItemStatus) !== -1
          ) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            count = count + orderProgress[orderItemStatus as OrderItemStatus];
          }
          return count;
        },
        0,
      );
      const statusGroupPercentage =
        statusGroupCount > 0 && order.orderItemsToProcessCount > 0
          ? Math.round(
              (statusGroupCount / order.orderItemsToProcessCount) * 100,
            )
          : 0;
      return {
        label: headerStatusGroup.name,
        percentage: statusGroupPercentage,
        key: headerStatusGroup.name.replace(/\s+/g, '-').toLowerCase(),
      };
    },
  );
};

export const getErrorMessagesFromCsvError = (
  submitCsvError: OrderSubmitCsvError | null,
  fallbackMessage: string,
) => {
  if (submitCsvError === null) {
    return '';
  }
  let errorMessage: string | string[] =
    submitCsvError?.message || fallbackMessage;
  if (
    submitCsvError instanceof ValidationErrorCsv &&
    submitCsvError.data.orderFile
  ) {
    const errorMessages: string[] = [];
    if (Array.isArray(submitCsvError.data.orderFile)) {
      submitCsvError.data.orderFile.forEach((orderFileError) => {
        errorMessages.push(orderFileError);
      });
    } else {
      errorMessages.push(submitCsvError.data.orderFile);
    }
    errorMessage = getErrorMessagesToShow(errorMessages);
  }
  return errorMessage;
};

export const getErrorMessagesFromEntryError = (
  submitEntryError: OrderSubmitEntryError | null,
  fallbackMessage: string,
): string | string[] => {
  if (submitEntryError === null) {
    return '';
  }
  let errorMessage: string | string[] =
    submitEntryError?.message || fallbackMessage;
  if (
    submitEntryError instanceof ValidationErrorAddressEntry &&
    submitEntryError.data.items
  ) {
    const errorMessages: string[] = [];
    submitEntryError.data.items.forEach((item) => {
      Object.entries(item).forEach(([key, value]: [string, string]) => {
        if (value !== null && value !== undefined) {
          // legacy had the value.toString(), not positive if it's still needed
          errorMessages.push(`${key}: ${value.toString()}`);
        }
      });
    });
    errorMessage = getErrorMessagesToShow(errorMessages);
  }

  return errorMessage;
};
